import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Box,
  makeStyles
} from '@material-ui/core';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import BusinessIcon from '@material-ui/icons/Business';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  title: {
    fontWeight: 700,
    fontSize: '1.6rem',
    marginBottom: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    fontSize: 80,
    color: theme.palette.warning.main,
  },
  statsBox: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    margin: theme.spacing(3, 0),
    border: `2px solid ${theme.palette.warning.light}`,
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  statIcon: {
    marginRight: theme.spacing(1.5),
    fontSize: '2rem',
  },
  statText: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
  },
  highlight: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  benefitsList: {
    textAlign: 'left',
    margin: theme.spacing(2, 'auto'),
    maxWidth: '400px',
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.success.dark,
  },
  benefitIcon: {
    marginRight: theme.spacing(1),
    fontSize: '1.5rem',
  },
  keepButton: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: theme.palette.success.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
    },
    transition: 'all 0.3s ease',
  },
  removeButton: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    textTransform: 'none',
  },
  savingsText: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    fontStyle: 'italic',
  },
}));

const CompanyLogoConfirmationDialog = ({ open, onKeepLogo, onRemoveLogo }) => {
  const classes = useStyles();

  return (
    <Dialog 
      open={open} 
      onClose={onKeepLogo} 
      aria-labelledby="logo-confirmation-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="logo-confirmation-dialog-title" className={classes.dialogTitle} disableTypography>
        <Typography variant="h4" className={classes.title} component="div">
          Wait! Don't Hide Your Brand <span role="img" aria-label="target">🎯</span>
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        <div className={classes.iconContainer}>
          <VisibilityOffIcon className={classes.warningIcon} />
        </div>

        <DialogContentText variant="h6" color="textPrimary" style={{ fontWeight: 600, marginBottom: 16 }}>
          Hiding your company logo significantly reduces your job's visibility
        </DialogContentText>

        <Box className={classes.statsBox}>
          <div className={classes.statItem}>
            <TrendingDownIcon className={classes.statIcon} style={{ color: '#f44336' }} />
            <Typography className={classes.statText}>
              <span className={classes.highlight}>70% fewer views</span> without a logo
            </Typography>
          </div>
          
          <div className={classes.statItem}>
            <TrendingDownIcon className={classes.statIcon} style={{ color: '#ff9800' }} />
            <Typography className={classes.statText}>
              <span className={classes.highlight}>3x less engagement</span> from candidates
            </Typography>
          </div>
          
          <div className={classes.statItem}>
            <BusinessIcon className={classes.statIcon} style={{ color: '#2196f3' }} />
            <Typography className={classes.statText}>
              Jobs <span className={classes.highlight}>with logos</span> build instant trust
            </Typography>
          </div>
        </Box>

        <DialogContentText style={{ marginTop: 24, fontSize: '1rem' }}>
          <strong>Why top companies always show their logo:</strong>
        </DialogContentText>

        <div className={classes.benefitsList}>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon} role="img" aria-label="sparkles">✨</span>
            <Typography variant="body1">Stand out from competitors instantly</Typography>
          </div>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon} role="img" aria-label="target">🎯</span>
            <Typography variant="body1">Attract higher quality candidates</Typography>
          </div>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon} role="img" aria-label="briefcase">💼</span>
            <Typography variant="body1">Build credibility and trust immediately</Typography>
          </div>
          <div className={classes.benefitItem}>
            <span className={classes.benefitIcon} role="img" aria-label="chart increasing">📈</span>
            <Typography variant="body1">Increase application rates by 2-3x</Typography>
          </div>
        </div>

        <Typography className={classes.savingsText}>
          For just $44, you're getting exponentially better results. That's less than $1.50/day for premium visibility! <span role="img" aria-label="light bulb">💡</span>
        </Typography>
      </DialogContent>

      <DialogActions style={{ justifyContent: 'center', paddingBottom: 24, flexDirection: 'column' }}>
        <Button
          onClick={onKeepLogo}
          variant="contained"
          className={classes.keepButton}
          startIcon={<BusinessIcon />}
        >
          Keep My Logo & Maximize Views
        </Button>
        <Button 
          onClick={onRemoveLogo} 
          className={classes.removeButton}
          size="small"
        >
          No thanks, hide my logo anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CompanyLogoConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onKeepLogo: PropTypes.func.isRequired,
  onRemoveLogo: PropTypes.func.isRequired,
};

export default CompanyLogoConfirmationDialog;

